import { SearchBar, Select, type Column } from "@legal-500/legal500-design-system";
import { useMemo, useState } from "react";
import type { Feature } from "../../api/features.service";
import { DataFilters } from "../../components/DataFilters/DataFilters";
import { DataTable } from "../../components/DataTable/DataTable";
import { SortableHeader } from "../../components/DataTable/SortableHeader";
import { DetailModal } from "../../components/DetailModal/DetailModal";
import EmptyState from "../../components/EmptyState";
import LoadingState from "../../components/LoadingState";
import { PageHeader } from "../../components/PageHeader";
import { PaginationControls } from "../../components/PaginationControls";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import PermissionBanner from "../../components/Permissions/PermissionBanner";
import { useFeatures } from "../../hooks/useFeatures";
import { useFilters } from "../../hooks/useFilters";
import { useUrlParams } from "../../hooks/useUrlParams";
import {
  COLUMN_CONFIGS,
  FILTER_CONFIGS,
  MODAL_FIELD_GROUPS,
  type SortField,
} from "./featureConfig";
import styles from "./Features.module.css";

type SortDirection = "asc" | "desc";

function FeaturesPage() {
  const { searchParams, updateUrl } = useUrlParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [filters, setFilters] = useState({
    type: searchParams.get("type") || "all",
    scope: searchParams.get("scope") || "all",
    status: searchParams.get("status") || "all",
  });
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);
  const [sortField, setSortField] = useState<SortField | null>(
    (searchParams.get("sortField") as SortField) || null,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    (searchParams.get("sortDirection") as SortDirection) || "asc",
  );

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters,
    updateUrl,
  });

  const handleSort = (field: SortField) => {
    let newDirection: SortDirection = "asc";
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortDirection(newDirection);
    updateUrl({ sortField: field, sortDirection: newDirection });
  };

  const { data: allFeatures, loading, error, refetch } = useFeatures();

  const filteredFeatures = useMemo(() => {
    const filtered = (allFeatures?.features || []).filter(feature => {
      const matchesSearch =
        searchTerm === "" ||
        feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.code.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = filters.type === "all" || feature.type === filters.type;
      const matchesScope =
        filters.scope === "all" ||
        (filters.scope === "null" && feature.scope === null) ||
        feature.scope === filters.scope;
      const matchesStatus = filters.status === "all" || feature.status === filters.status;

      return matchesSearch && matchesType && matchesScope && matchesStatus;
    });

    if (sortField) {
      filtered.sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];

        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return sortDirection === "asc" ? 1 : -1;
        if (bValue === null) return sortDirection === "asc" ? -1 : 1;

        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) return sortDirection === "asc" ? -1 : 1;
        if (aStr > bStr) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    setCurrentPage(1);
    return filtered;
  }, [allFeatures, searchTerm, filters, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredFeatures.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFeatures = filteredFeatures.slice(startIndex, endIndex);

  const columns: Column<Feature>[] = COLUMN_CONFIGS.map(config => ({
    header: config.sortable
      ? ((
          <SortableHeader
            label={config.header}
            field={config.sortField! as SortField}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        ) as unknown as string)
      : config.header,
    accessor: config.accessor,
    render: config.render
      ? (value: unknown, row: Feature) =>
          config.render!(value as Feature[typeof config.accessor], row)
      : undefined,
  }));

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Features" subtitle="View platform features loaded from JSON files." />
        <LoadingState message="Loading features..." />
      </div>
    );
  }

  // Check for 401 Unauthorized or 403 Forbidden error
  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }

    // Show error state for other errors
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Features" subtitle="View platform features loaded from JSON files." />
        <EmptyState
          variant="error"
          title="Failed to load features"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => refetch(),
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Features" subtitle="View platform features loaded from JSON files." />
        <PermissionBanner scope="fulfilment" resource="features" />
      </div>

      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={value => {
            setSearchTerm(value);
            updateUrl({ search: value });
          }}
          onClear={() => {
            setSearchTerm("");
            updateUrl({ search: "" });
          }}
          placeholder="Search features..."
          className={styles.searchBar}
        />
      </div>

      <DataFilters
        filters={FILTER_CONFIGS}
        values={filters}
        onChange={handleFilterChange}
        activeFilters={activeFilters}
        onClearFilter={handleClearFilter}
      >
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="itemsPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <Select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={e => {
              const newPageSize = Number(e.target.value);
              setItemsPerPage(newPageSize);
              setCurrentPage(1);
              updateUrl({ pageSize: String(newPageSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </DataFilters>
      <div className={styles.tableContainer}>
        <DataTable
          data={paginatedFeatures}
          columns={columns}
          onRowClick={setSelectedFeature}
          rowKey="code"
        />
      </div>
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        startIndex={startIndex}
        endIndex={endIndex}
        totalCount={filteredFeatures.length}
        itemName="features"
        onPageChange={page => {
          setCurrentPage(page);
          updateUrl({ page: String(page) });
        }}
      />

      <DetailModal
        isOpen={!!selectedFeature}
        onClose={() => setSelectedFeature(null)}
        item={selectedFeature}
        title={selectedFeature?.name}
        fieldGroups={MODAL_FIELD_GROUPS}
        renderValue={(feature: Feature, key: keyof Feature) => {
          const allFields = MODAL_FIELD_GROUPS.flatMap(g => g.fields);
          const field = allFields.find(f => f.key === key);
          if (field?.render) {
            return field.render(feature[key], feature);
          }
          return String(feature[key] ?? "");
        }}
      />
    </div>
  );
}

export default FeaturesPage;
